/**
 * GroupParticipants.js
 * Component for displaying participants of a specific group
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Badge, 
  Alert,
  Spinner,
  Button,
  Modal,
  Form,
  Dropdown
} from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import moment from 'moment';
import { groupService, userService, subscriptionService, courseOfferService, rollCallService } from '../../services/api';
import { 
  isSubscriptionValid, 
  getSubscriptionStatus,
  getSubscriptionStatusBadge, 
  getValidSubscriptions, 
  countExpiredSubscriptions,
  generateDefaultInstallments,
  createInstallmentsFromOfferDefaults,
  shiftInstallmentDueDates,
  calculateInstallmentsSum,
  getInstallmentCoveragePeriod,
  formatOfferLabel,
  formatOfferType,
  isPayPerUseOffer,
  isPayPerUseSubscription,
  isTrialSubscription,
  isMonthlyOffer,
  PAYMENT_METHODS,
  formatPaymentMethod
} from '../../helpers/subscriptionHelpers';
import { useDynamicBreadcrumb } from '../../helpers/useDynamicBreadcrumb';
import ExportCSVButton from '../Common/ExportCSVButton';
import { exportToCSV } from '../../utils/csvExport';
import EditSubscriptionModal from '../Subscriptions/EditSubscriptionModal';
import BulkUserActions from '../Users/BulkUserActions';

const GroupParticipants = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(''); // Errori critici (caricamento, gruppo non trovato)
  const [actionError, setActionError] = useState(''); // Errori di azioni sui partecipanti
  const [success, setSuccess] = useState('');

  // Breadcrumb dinamico con il nome del gruppo
  useDynamicBreadcrumb(
    location.pathname,
    group ? [
      { label: 'Gruppi', path: '/groups', icon: 'fas fa-users' },
      { label: group.name, path: null, icon: 'fas fa-user-friends' }
    ] : null
  );

  // Modal per aggiunta partecipanti
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isNewSubscriptionMode, setIsNewSubscriptionMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isInstallmentPlan, setIsInstallmentPlan] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [isCustomPayPerUse, setIsCustomPayPerUse] = useState(false);
  const [totalTickets, setTotalTickets] = useState(1);
  const [subscriptionData, setSubscriptionData] = useState({
    price: 0,
    startDate: new Date().toISOString().split('T')[0], // Data odierna
    endDate: '',
    notes: '',
    usedEntriesAdjustment: 0,
    paymentDate: '',
    paymentMethod: ''
  });

  // Modal gestione rapida rate
  const [showInstallmentsModal, setShowInstallmentsModal] = useState(false);
  const [selectedSubscriptionForInstallments, setSelectedSubscriptionForInstallments] = useState(null);
  const [payingInstallment, setPayingInstallment] = useState(null);

  // Loading states per azioni
  const [actionLoading, setActionLoading] = useState({});
  
  // Errore specifico per la modale
  const [modalError, setModalError] = useState('');
  
  // Modale di avviso per abbonamenti incompatibili
  const [showIncompatibleModal, setShowIncompatibleModal] = useState(false);
  const [incompatibleSubscriptions, setIncompatibleSubscriptions] = useState([]);
  
  // Mostra/Nascondi atleti archiviati
  const [participantFilter, setParticipantFilter] = useState('active'); // 'active' | 'archived' | 'all'

  // Selezione atleti per invio azioni massive (email, link portale)
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  
  // Ref per il debounce
  const searchTimeoutRef = useRef(null);
  
  // State per mappare subscription ID a status ingressi (pay-per-use)
  const [entriesStatusMap, setEntriesStatusMap] = useState({});

  // Subscription per cui lo status ingressi è già stato richiesto (evita richieste duplicate e loop di retry)
  const requestedEntriesRef = useRef(new Set());

  // Export state
  const [exporting, setExporting] = useState(false);

  // Handle CSV export
  const handleExportCSV = async () => {
    setExporting(true);
    setError('');
    
    try {
      if (!visibleSubscriptions || visibleSubscriptions.length === 0) {
        return;
      }

      // Usa i dati visibili in base al filtro
      const exportData = visibleSubscriptions.map(g => {
        const sub = g.primarySubscription;
        const entriesStatus = sub?.id ? entriesStatusMap[sub.id] : undefined;
        
        return {
          ...g,
          subscription: sub,
          entriesStatus
        };
      });

      const columns = [
        { label: 'Nome', accessor: (d) => d.user?.firstName || '' },
        { label: 'Cognome', accessor: (d) => d.user?.lastName || '' },
        { label: 'Email', accessor: (d) => d.user?.email || '' },
        { label: 'Abbonamento', accessor: (d) => {
          if (isTrialSubscription(d.subscription)) return 'Prova';
          return d.subscription?.offer?.name || (d.subscription ? 'Data Libera' : 'Nessun abbonamento');
        }},
        { label: 'Tipo', accessor: (d) => {
          if (!d.subscription) return 'Nessun abbonamento';
          if (isTrialSubscription(d.subscription)) return 'Prova';
          if (!d.subscription.offer) {
            return d.subscription.totalTickets ? `Pay-per-use (${d.subscription.totalTickets} ingressi)` : 'Data Libera';
          }
          return formatOfferType(d.subscription.offer);
        }},
        { label: 'Ingressi', accessor: (d) => {
          if (!d.entriesStatus || d.entriesStatus.usedEntries == null || d.entriesStatus.totalEntries == null) {
            return '';
          }
          return `${d.entriesStatus.usedEntries}/${d.entriesStatus.totalEntries}`;
        }},
        { label: 'Prezzo', accessor: (d) => d.subscription?.price != null ? `€${d.subscription.price.toFixed(2)}` : '€0.00' },
        { label: 'Data Inizio', accessor: (d) => d.subscription?.startDate ? moment(d.subscription.startDate).format('DD/MM/YYYY') : 'N/A' },
        { label: 'Data Fine', accessor: (d) => d.subscription?.endDate ? moment(d.subscription.endDate).format('DD/MM/YYYY') : 'N/A' },
        { label: 'Stato', accessor: (d) => {
          if (!d.subscription) return 'N/A';
          const statusBadge = getSubscriptionStatusBadge(d.subscription);
          return statusBadge?.text || 'N/A';
        }},
        { label: 'Archiviato', accessor: (d) => d.isArchived ? 'Sì' : 'No' },
        { label: 'Note', accessor: (d) => d.subscription?.notes || '' },
        { label: 'Data Pagamento', accessor: (d) => d.subscription?.paymentDate ? moment(d.subscription.paymentDate).format('DD/MM/YYYY') : '' },
        { label: 'Metodo Pagamento', accessor: (d) => formatPaymentMethod(d.subscription?.paymentMethod) }
      ];
      
      const groupName = (group?.name || 'gruppo').replace(/\s+/g, '_');
      exportToCSV(exportData, `partecipanti_${groupName}`, columns);
      setSuccess(`${exportData.length} partecipanti esportati con successo`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Export error:', error);
      setError('Errore durante l\'esportazione dei partecipanti');
    } finally {
      setExporting(false);
    }
  };

  // Invalida lo status ingressi memorizzato per le subscription indicate, così da poterlo ricaricare
  const invalidateEntriesStatus = useCallback((subscriptionIds) => {
    if (!subscriptionIds || subscriptionIds.length === 0) {
      return;
    }

    subscriptionIds.forEach(subscriptionId => requestedEntriesRef.current.delete(subscriptionId));

    setEntriesStatusMap(prev => {
      const updated = { ...prev };
      subscriptionIds.forEach(subscriptionId => {
        delete updated[subscriptionId];
      });
      return updated;
    });
  }, []);

  // Funzione per caricare status ingressi per subscription pay-per-use
  const loadEntriesStatus = useCallback(async (subscriptions) => {
    // Carica solo le subscription pay-per-use mai richieste prima:
    // una subscription già richiesta (anche se in errore) non viene ricaricata automaticamente,
    // altrimenti si genera un ciclo infinito di richieste.
    const subscriptionsToLoad = (subscriptions || []).filter(sub =>
      isPayPerUseSubscription(sub) && sub.user?.id && !requestedEntriesRef.current.has(sub.id)
    );

    if (subscriptionsToLoad.length === 0) {
      return;
    }

    subscriptionsToLoad.forEach(sub => requestedEntriesRef.current.add(sub.id));

    setEntriesStatusMap(prev => {
      const updated = { ...prev };
      subscriptionsToLoad.forEach(sub => {
        updated[sub.id] = { loading: true };
      });
      return updated;
    });

    // La query restituisce lo status di tutte le subscription richieste: una sola chiamata per utente
    const subscriptionIdsByUser = subscriptionsToLoad.reduce((acc, sub) => {
      (acc[sub.user.id] = acc[sub.user.id] || []).push(sub.id);
      return acc;
    }, {});
    const userIds = Object.keys(subscriptionIdsByUser);

    try {
      const results = await Promise.all(
        userIds.map(userId =>
          rollCallService.getUserEntriesStatus(userId, null, subscriptionIdsByUser[userId])
        )
      );

      const resultsByUser = new Map();
      userIds.forEach((userId, index) => resultsByUser.set(userId, results[index]));

      setEntriesStatusMap(prev => {
        const updated = { ...prev };
        subscriptionsToLoad.forEach(sub => {
          const result = resultsByUser.get(sub.user.id);
          const specificSub = result?.success
            ? result.data?.subscriptions?.find(s => s.subscriptionId === sub.id)
            : null;

          if (specificSub) {
            updated[sub.id] = {
              loading: false,
              totalEntries: specificSub.totalEntries,
              usedEntries: specificSub.usedEntries,
              remainingEntries: specificSub.remainingEntries,
              isRunningOut: specificSub.isRunningOut,
              hasActiveMonthlySubscription: result.data.hasActiveMonthlySubscription,
              academicYear: result.data.academicYear
            };
          } else {
            updated[sub.id] = { loading: false, error: true };
          }
        });
        return updated;
      });
    } catch (err) {
      console.error('Error loading entries status:', err);
      setEntriesStatusMap(prev => {
        const updated = { ...prev };
        subscriptionsToLoad.forEach(sub => {
          updated[sub.id] = { loading: false, error: true };
        });
        return updated;
      });
    }
  }, []); // Nessuna dipendenza per evitare loop infiniti

  // Carica i dati del gruppo e i partecipanti
  const loadGroupData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await groupService.getGroup(id);
      
      if (response?.success) {
        setGroup(response.data);
        setError('');
      } else {
        setError(response.message || 'Errore nel caricamento del gruppo');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading group data:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadGroupData();
    }
  }, [id, loadGroupData]);

  // Reset della cache ingressi quando cambia il gruppo (il componente non viene rimontato)
  useEffect(() => {
    requestedEntriesRef.current = new Set();
    setEntriesStatusMap({});
  }, [id]);

  // Cleanup del timeout quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Carica utenti disponibili per l'aggiunta con debounce
  const loadUsersAsync = (inputValue) => {
    return new Promise((resolve) => {
      // Cancella il timeout precedente
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Imposta nuovo timeout per il debounce
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          if (!inputValue || inputValue.length < 2) {
            resolve([]);
            return;
          }

          const response = await userService.getUsers({
            search: inputValue,
            page: 1,
            limit: 50
          });
          
          if (response?.success) {
            // Filtra utenti che non sono già iscritti al gruppo (con almeno un iscrizione valido)
            const currentUserIds = getValidSubscriptions(group?.courseSubscriptions || [])
              .map(sub => sub.user.id);
            const filteredUsers = response.data.items.filter(user => 
              !currentUserIds.includes(user.id)
            );
            
            // Trasforma in formato react-select
            const options = filteredUsers.map(user => ({
              value: user.id,
              label: `${user.firstName} ${user.lastName} - ${user.email}`,
              user: user
            }));
            
            resolve(options);
          } else {
            resolve([]);
          }
        } catch (err) {
          console.error('Error loading users:', err);
          resolve([]);
        }
      }, 300); // 300ms di debounce
    });
  };

  // Carica gli abbonamenti per AsyncSelect
  const loadOffersAsync = async (inputValue = '') => {
    if (!group?.course?.id) return [];
    
    try {
      const response = await courseOfferService.getCourseOffersSummary(group.course.id, inputValue);
      
      const trialOption = { 
        value: 'TRIAL', 
        label: 'Prova', 
        offer: { name: 'Prova', isTrial: true, amount: 0 } 
      };

      const freeDateOption = { 
        value: 'FREE_DATE', 
        label: 'Abbonamento a data libera', 
        offer: { name: 'Data Libera', isFreeDate: true, amount: 0 } 
      };

      const matches = (label, query) => !query || label.toLowerCase().includes(query.toLowerCase());
      const systemOptions = [trialOption, freeDateOption].filter(opt => matches(opt.label, inputValue));

      if (response?.success) {
        const offerOptions = response.data.map(offer => ({
          value: offer.id,
          label: formatOfferLabel(offer),
          offer: {
            ...offer,
            durationMonths: offer.durationMonths,
            totalTickets: offer.totalTickets
          }
        }));
        return [...systemOptions, ...offerOptions];
      }
      return systemOptions;
    } catch (err) {
      console.error('Error loading available offers:', err);
      return [];
    }
  };

  // Gestori per le azioni sui partecipanti

  const handleArchiveUser = async (userId, userName, isArchived) => {
    const action = isArchived ? 'archiviare' : 'ripristinare';
    if (!window.confirm(`Sei sicuro di voler ${action} ${userName}?`)) {
      return;
    }

    const key = `archive-${userId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const response = await subscriptionService.archiveUserInGroup(id, userId, isArchived);

      if (response?.success) {
        setSuccess(response.message);
        await loadGroupData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setActionError(response.message);
      }
    } catch (err) {
      setActionError(`Errore nell'${isArchived ? 'archiviazione' : 'ripristino'} dell'atleta`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveParticipant = async (subscription) => {
    if (!window.confirm(`Sei sicuro di voler rimuovere questa iscrizione di ${subscription.user.firstName} ${subscription.user.lastName}?`)) {
      return;
    }
    
    const key = `remove-${subscription.id}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await subscriptionService.deleteSubscription(subscription.id);
      
      if (response?.success) {
        setSuccess('Partecipante rimosso con successo');
        
        // Se l'abbonamento rimosso era pay-per-use, pulisci lo status dalla mappa
        if (isPayPerUseSubscription(subscription)) {
          invalidateEntriesStatus([subscription.id]);
        }
        
        await loadGroupData(); // Ricarica i dati
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setActionError(response.message);
      }
    } catch (err) {
      setActionError('Errore nella rimozione del partecipante');
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAddParticipant = async () => {
    // Validazione: solo utente e date sono obbligatori
    if (!selectedUser || !subscriptionData.startDate || !subscriptionData.endDate) {
      setActionError('Compila tutti i campi obbligatori (utente e date)');
      return;
    }

    const isTrialMode = selectedOffer?.value === 'TRIAL';

    if ((isTrialMode || isCustomPayPerUse) && (!totalTickets || parseInt(totalTickets, 10) < 1)) {
      setActionError('Inserisci un numero valido di ingressi (almeno 1)');
      return;
    }

    if (!isTrialMode && isInstallmentPlan && hasSumMismatch) {
      setActionError('Impossibile creare l\'iscrizione: la somma delle rate non corrisponde al prezzo totale.');
      return;
    }

    if (!isTrialMode && isInstallmentPlan) {
      for (const inst of installments) {
        if (!inst.dueDate) {
          setActionError(`Compila la data di scadenza per la Rata ${inst.installmentNumber}`);
          return;
        }
        if (inst.amount < 0 || isNaN(parseFloat(inst.amount))) {
          setActionError(`Importo non valido per la Rata ${inst.installmentNumber}`);
          return;
        }
      }
    }
    
    setActionLoading(prev => ({ ...prev, 'add-participant': true }));
    
    try {
      const finalPrice = isTrialMode ? 0 : (parseFloat(subscriptionData.price) || 0);
      const finalOfferId = (selectedOffer?.value === 'FREE_DATE' || selectedOffer?.value === 'TRIAL') ? null : selectedOffer?.value;
      const finalTotalTickets = isTrialMode 
        ? (parseInt(totalTickets, 10) || 1) 
        : (isCustomPayPerUse ? (parseInt(totalTickets, 10) || null) : (selectedOffer?.offer?.totalTickets ?? null));
      const finalHasInstallments = !isTrialMode && !!isInstallmentPlan;
      
      let response;
      
      if (isEditMode && editingSubscription) {
        // Modalità modifica
        response = await subscriptionService.updateSubscription({
          id: editingSubscription.id,
          groupId: group.id,
          price: finalPrice,
          startDate: subscriptionData.startDate,
          endDate: subscriptionData.endDate,
          offerId: finalOfferId,
          totalTickets: finalTotalTickets,
          isTrial: isTrialMode,
          notes: subscriptionData.notes,
          usedEntriesAdjustment: isTrialMode ? 0 : subscriptionData.usedEntriesAdjustment,
          paymentDate: finalHasInstallments || isTrialMode ? null : (subscriptionData.paymentDate || null),
          paymentMethod: finalHasInstallments
            ? (installments.some(i => i.paymentDate) ? (subscriptionData.paymentMethod || 'cash') : null)
            : (subscriptionData.paymentDate ? (subscriptionData.paymentMethod || 'cash') : null),
          hasInstallments: finalHasInstallments,
          installments: finalHasInstallments ? installments.map(i => ({
            installmentNumber: i.installmentNumber,
            amount: parseFloat(i.amount) || 0,
            dueDate: i.dueDate,
            paymentDate: i.paymentDate || null,
            notes: i.notes || null
          })) : null
        });
        
        // Gestisci incompatibilità anche in modifica
        if (!response.success && response.isIncompatibleSubscriptions) {
          const payPerUseResponse = await subscriptionService.getActivePayPerUseSubscriptions(
            editingSubscription.user.id || selectedUser.value,
            group.id,
            subscriptionData.startDate,
            subscriptionData.endDate
          );
          
          if (payPerUseResponse.success && payPerUseResponse.data.length > 0) {
            setIncompatibleSubscriptions(payPerUseResponse.data);
            setShowAddModal(false);
            setShowIncompatibleModal(true);
            return;
          }
        }
      } else {
        // Modalità creazione
        response = await subscriptionService.createSubscription({
          userId: selectedUser.value,
          groupId: group.id,
          price: finalPrice,
          startDate: subscriptionData.startDate,
          endDate: subscriptionData.endDate,
          offerId: finalOfferId,
          totalTickets: finalTotalTickets,
          isTrial: isTrialMode,
          notes: subscriptionData.notes,
          usedEntriesAdjustment: isTrialMode ? 0 : subscriptionData.usedEntriesAdjustment,
          paymentDate: finalHasInstallments || isTrialMode ? null : (subscriptionData.paymentDate || null),
          paymentMethod: finalHasInstallments
            ? (installments.some(i => i.paymentDate) ? (subscriptionData.paymentMethod || 'cash') : null)
            : (subscriptionData.paymentDate ? (subscriptionData.paymentMethod || 'cash') : null),
          hasInstallments: finalHasInstallments,
          installments: finalHasInstallments ? installments.map(i => ({
            installmentNumber: i.installmentNumber,
            amount: parseFloat(i.amount) || 0,
            dueDate: i.dueDate,
            paymentDate: i.paymentDate || null,
            notes: i.notes || null
          })) : null
        });
      }
      
      if (response?.success) {
        setSuccess(isEditMode ? 'Iscrizione modificata con successo' : 'Partecipante aggiunto con successo');
        setShowAddModal(false);
        resetAddModal();
        await loadGroupData(); // Ricarica i dati
        
        // Se l'abbonamento è pay-per-use, ricarica lo status ingressi dell'utente
        const isPayPerUse = isTrialMode || isCustomPayPerUse || isPayPerUseSubscription(selectedOffer?.offer) || isPayPerUseSubscription(editingSubscription);
        if (isPayPerUse) {
          // Rimuovi lo status precedente per forzare il ricaricamento
          const updatedUserId = isEditMode ? editingSubscription.user.id : selectedUser.value;
          invalidateEntriesStatus(
            (group?.courseSubscriptions || [])
              .filter(sub => sub.user?.id === updatedUserId)
              .map(sub => sub.id)
          );
        }
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Verifica se è un errore di incompatibilità tra abbonamenti
        if (response.isIncompatibleSubscriptions) {
          // Carica gli abbonamenti a ingressi esistenti che si sovrappongono temporalmente
          const payPerUseResponse = await subscriptionService.getActivePayPerUseSubscriptions(
            selectedUser.value, 
            group.id,
            subscriptionData.startDate,
            subscriptionData.endDate
          );
          
          if (payPerUseResponse.success && payPerUseResponse.data.length > 0) {
            setIncompatibleSubscriptions(payPerUseResponse.data);
            setShowAddModal(false);
            setShowIncompatibleModal(true);
          } else {
            setActionError(response.message);
          }
        } else {
          setActionError(response.message);
        }
      }
    } catch (err) {
      setActionError(isEditMode ? 'Errore nella modifica dell\'iscrizione' : 'Errore nell\'aggiunta del partecipante');
    } finally {
      setActionLoading(prev => ({ ...prev, 'add-participant': false }));
    }
  };

  const resetAddModal = () => {
    setSelectedUser(null);
    setSelectedOffer(null);
    setIsInstallmentPlan(false);
    setInstallments([]);
    setIsCustomPayPerUse(false);
    setTotalTickets(1);
    setSubscriptionData({
      price: 0,
      startDate: new Date().toISOString().split('T')[0], // Data odierna
      endDate: '',
      notes: '',
      usedEntriesAdjustment: 0,
      paymentDate: '',
      paymentMethod: ''
    });
    
    // Cancella timeout del debounce
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleCloseIncompatibleModal = () => {
    setShowIncompatibleModal(false);
    setIncompatibleSubscriptions([]);
    resetAddModal();
  };

  // Gestione rate per l'aggiunta partecipante
  const handleToggleInstallmentPlan = (enableInstallments) => {
    setIsInstallmentPlan(enableInstallments);
    if (enableInstallments && selectedOffer?.offer) {
      const offer = selectedOffer.offer;
      const totalAmount = offer.installmentTotalAmount != null
        ? offer.installmentTotalAmount
        : offer.amount;

      let generated;
      if (offer.defaultInstallments && offer.defaultInstallments.length > 0) {
        generated = createInstallmentsFromOfferDefaults(offer.defaultInstallments);
      } else {
        const count = offer.installmentCount || 2;
        generated = generateDefaultInstallments(
          subscriptionData.startDate,
          subscriptionData.endDate,
          count,
          totalAmount
        );
      }

      setInstallments(generated);
      setSubscriptionData(prev => ({
        ...prev,
        price: totalAmount
      }));
    } else if (selectedOffer?.offer) {
      setInstallments([]);
      setSubscriptionData(prev => ({
        ...prev,
        price: selectedOffer.offer.amount
      }));
    }
  };

  const handleInstallmentChange = (index, field, value) => {
    setInstallments(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    if (field === 'paymentDate') {
      setSubscriptionData(prev => {
        const remainingPaid = installments.some((inst, i) => (i === index ? !!value : !!inst.paymentDate));
        return {
          ...prev,
          paymentMethod: remainingPaid ? (prev.paymentMethod || 'cash') : ''
        };
      });
    }
  };

  const handleMarkInstallmentPaidToday = (index) => {
    const today = new Date().toISOString().split('T')[0];
    handleInstallmentChange(index, 'paymentDate', today);
  };

  const handleClearInstallmentPayment = (index) => {
    handleInstallmentChange(index, 'paymentDate', '');
  };

  const installmentsSum = isInstallmentPlan
    ? calculateInstallmentsSum(installments)
    : 0;
  const currentPrice = parseFloat(subscriptionData.price) || 0;
  const sumDiff = Math.round((installmentsSum - currentPrice) * 100) / 100;
  const hasSumMismatch = Boolean(isInstallmentPlan && Math.abs(sumDiff) > 0.01);

  const handleAdaptPriceToSum = () => {
    setSubscriptionData(prev => ({
      ...prev,
      price: installmentsSum
    }));
  };

  const handleRecalculateEqualInstallments = () => {
    const count = installments.length;
    if (count === 0) return;
    const total = parseFloat(subscriptionData.price) || 0;
    const baseAmount = Math.floor((total / count) * 100) / 100;
    const remainder = Math.round((total - (baseAmount * count)) * 100) / 100;
    setInstallments(prev => prev.map((inst, idx) => ({
      ...inst,
      amount: idx === count - 1 ? Math.round((baseAmount + remainder) * 100) / 100 : baseAmount
    })));
  };

  // Quick pay installment from modal
  const handleStartQuickPayInstallment = (installment) => {
    setPayingInstallment({
      installment,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: selectedSubscriptionForInstallments?.paymentMethod || 'cash'
    });
  };

  const handleConfirmQuickPayInstallment = async () => {
    if (!payingInstallment) return;
    const { installment, paymentDate, paymentMethod } = payingInstallment;
    await executeQuickPay(installment.id, paymentDate, paymentMethod);
    setPayingInstallment(null);
  };

  const executeQuickPay = async (installmentId, paymentDate, paymentMethod) => {
    const key = `pay-inst-${installmentId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const pDate = paymentDate || new Date().toISOString();
      const pMethod = paymentMethod || selectedSubscriptionForInstallments?.paymentMethod || 'cash';
      const response = await subscriptionService.payInstallment(installmentId, pDate, pMethod);
      if (response?.success) {
        setSuccess('Pagamento della rata registrato con successo');
        await loadGroupData();
        setSelectedSubscriptionForInstallments(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            paymentMethod: pMethod,
            installments: (prev.installments || []).map(i =>
              i.id === installmentId ? { ...i, paymentDate: pDate } : i
            )
          };
        });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setActionError(response.message || 'Errore nella registrazione del pagamento');
      }
    } catch (err) {
      console.error('Error paying installment:', err);
      setActionError('Errore nella registrazione del pagamento');
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleQuickPayInstallment = (installmentOrId) => {
    if (typeof installmentOrId === 'object' && installmentOrId !== null) {
      handleStartQuickPayInstallment(installmentOrId);
    } else {
      const found = (selectedSubscriptionForInstallments?.installments || []).find(i => i.id === installmentOrId);
      if (found) {
        handleStartQuickPayInstallment(found);
      } else {
        executeQuickPay(installmentOrId);
      }
    }
  };

  // Gestisce la selezione dell'abbonamento e aggiorna il prezzo
  const handleOfferSelect = (selectedOption) => {
    setSelectedOffer(selectedOption);
    if (selectedOption?.value === 'TRIAL') {
      const startDate = new Date(subscriptionData.startDate || new Date().toISOString().split('T')[0]);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      const endDateStr = endDate.toISOString().split('T')[0];

      setIsInstallmentPlan(false);
      setInstallments([]);
      setIsCustomPayPerUse(false);
      setTotalTickets(1);
      setSubscriptionData(prev => ({
        ...prev,
        price: 0,
        endDate: endDateStr
      }));
      return;
    }

    if (selectedOption?.offer) {
      // Calcola la data di fine in base al tipo di abbonamento
      const startDate = new Date(subscriptionData.startDate);
      let endDate;
      
      if (selectedOption.value === 'FREE_DATE' || (!isPayPerUseOffer(selectedOption.offer) && !isMonthlyOffer(selectedOption.offer))) {
        // Data Libera: default + 7 giorni
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 7);
        setIsCustomPayPerUse(false);
        setTotalTickets('');
      } else if (isPayPerUseOffer(selectedOption.offer)) {
        // Pay-per-use: aggiungi 1 anno
        endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        setIsCustomPayPerUse(false);
        setTotalTickets(selectedOption.offer.totalTickets || '');
      } else if (isMonthlyOffer(selectedOption.offer) && selectedOption.offer.durationMonths) {
        // Iscrizione mensile: aggiungi i mesi specificati
        endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + selectedOption.offer.durationMonths);
        setIsCustomPayPerUse(false);
        setTotalTickets('');
      } else {
        // Errore: abbonamento non valido
        setActionError('Abbonamento non valido: mancano informazioni sulla durata');
        return;
      }
      
      const endDateStr = endDate.toISOString().split('T')[0];

      let newPrice = selectedOption.offer.amount;
      if (selectedOption.offer.hasInstallments && isInstallmentPlan) {
        newPrice = selectedOption.offer.installmentTotalAmount != null
          ? selectedOption.offer.installmentTotalAmount
          : selectedOption.offer.amount;
        let generated;
        if (selectedOption.offer.defaultInstallments && selectedOption.offer.defaultInstallments.length > 0) {
          generated = createInstallmentsFromOfferDefaults(selectedOption.offer.defaultInstallments);
        } else {
          const count = selectedOption.offer.installmentCount || 2;
          generated = generateDefaultInstallments(subscriptionData.startDate, endDateStr, count, newPrice);
        }
        setInstallments(generated);
      } else {
        setIsInstallmentPlan(false);
        setInstallments([]);
      }

      setSubscriptionData(prev => ({
        ...prev,
        price: newPrice,
        endDate: endDateStr
      }));
    } else {
      setIsInstallmentPlan(false);
      setInstallments([]);
      setIsCustomPayPerUse(false);
      setTotalTickets(1);
      setSubscriptionData(prev => ({
        ...prev,
        price: 0,
        endDate: ''
      }));
    }
  };

  // Gestisce il cambio della data di inizio e ricalcola la data di fine se c'è un'abbonamento selezionata
  const handleStartDateChange = (newStartDate) => {
    const prevStartDateStr = subscriptionData.startDate;
    setSubscriptionData(prev => ({
      ...prev,
      startDate: newStartDate
    }));

    // Se c'è un'abbonamento selezionata, ricalcola la data di fine
    if (selectedOffer?.value === 'TRIAL') {
      const startDate = new Date(newStartDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      setSubscriptionData(prev => ({
        ...prev,
        endDate: endDate.toISOString().split('T')[0]
      }));
      return;
    }

    if (selectedOffer?.offer) {
      const startDate = new Date(newStartDate);
      let endDate;
      
      if (selectedOffer.value === 'FREE_DATE' || (!isPayPerUseOffer(selectedOffer.offer) && !isMonthlyOffer(selectedOffer.offer))) {
        // Data Libera: default + 7 giorni
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 7);
      } else if (isPayPerUseOffer(selectedOffer.offer)) {
        // Pay-per-use: aggiungi 1 anno
        endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
      } else if (isMonthlyOffer(selectedOffer.offer) && selectedOffer.offer.durationMonths) {
        // Iscrizione mensile: aggiungi i mesi specificati
        endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + selectedOffer.offer.durationMonths);
      } else {
        return;
      }
      
      const endDateStr = endDate.toISOString().split('T')[0];

      if (isInstallmentPlan && selectedOffer.offer.hasInstallments) {
        if (selectedOffer.offer.defaultInstallments && selectedOffer.offer.defaultInstallments.length > 0) {
          // Trasla le date delle rate dello stesso numero di giorni di cui è stata spostata la Data Inizio
          if (prevStartDateStr && newStartDate) {
            const prevDate = new Date(prevStartDateStr);
            const nextDate = new Date(newStartDate);
            const diffDays = Math.round((nextDate.getTime() - prevDate.getTime()) / 86400000);
            if (diffDays !== 0) {
              setInstallments(prev => shiftInstallmentDueDates(prev, diffDays));
            }
          }
        } else {
          const count = selectedOffer.offer.installmentCount || installments.length || 2;
          const total = parseFloat(subscriptionData.price) || selectedOffer.offer.installmentTotalAmount || selectedOffer.offer.amount;
          const regenerated = generateDefaultInstallments(newStartDate, endDateStr, count, total);
          setInstallments(prev => regenerated.map((inst, idx) => ({
            ...inst,
            amount: prev[idx]?.amount != null ? prev[idx].amount : inst.amount,
            paymentDate: prev[idx]?.paymentDate || '',
            notes: prev[idx]?.notes || ''
          })));
        }
      }

      setSubscriptionData(prev => ({
        ...prev,
        endDate: endDateStr
      }));
    }
  };

  const handleShowAddModal = () => {
    setIsNewSubscriptionMode(false);
    setShowAddModal(true);
  };

  const handleShowNewSubscriptionModal = (user) => {
    setIsNewSubscriptionMode(true);
    setIsEditMode(false);
    setSelectedUser({
      value: user.id,
      label: `${user.firstName} ${user.lastName} - ${user.email}`,
      user: user
    });
    
    // Normalizza la data odierna (mezzanotte UTC)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0];
    
    // Calcola la data di inizio del nuovo iscrizione
    let newStartDate = todayString; // Default: oggi
    
    // Trova l'ultimo iscrizione valido per questo utente
    // Escludi gli abbonamenti pay-per-use (a ingressi) perché hanno validità lunga
    // ma si esauriscono prima della scadenza
    const userSubscriptions = group?.courseSubscriptions?.filter(sub => 
      sub.user.id === user.id && 
      sub.endDate && 
      !isPayPerUseSubscription(sub)
    ) || [];
    
    if (userSubscriptions.length > 0) {
      // Ordina per data di fine decrescente per trovare l'ultimo iscrizione
      const sortedSubscriptions = userSubscriptions.sort((a, b) => 
        new Date(b.endDate) - new Date(a.endDate)
      );
      
      const lastSubscription = sortedSubscriptions[0];
      const lastEndDate = new Date(lastSubscription.endDate);
      lastEndDate.setHours(0, 0, 0, 0);
      
      // Imposta la data di inizio al giorno successivo solo se è >= oggi
      if (lastEndDate >= today) {
        const nextDay = new Date(lastEndDate);
        nextDay.setDate(nextDay.getDate() + 1);
        newStartDate = nextDay.toISOString().split('T')[0];
      }
      // Altrimenti mantieni la data odierna (iscrizione precedente già scaduta)
    }
    
    // Imposta i dati dell'iscrizione con la data calcolata
    setSubscriptionData({
      price: 0,
      startDate: newStartDate,
      endDate: '',
      notes: '',
      paymentDate: '',
      paymentMethod: ''
    });
    
    setShowAddModal(true);
  };

  const handleShowEditSubscriptionModal = (subscription) => {
    setEditingSubscription(subscription);
    setShowEditModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setIsNewSubscriptionMode(false);
    setIsEditMode(false);
    setEditingSubscription(null);
    setModalError(''); // Pulisci l'errore della modale
    setActionError(''); // Pulisci gli errori di azione
    setSuccess(''); // Pulisci i messaggi di successo
    resetAddModal();
  };

  // Raggruppa le iscrizioni per utente e mostra quella valida per la data odierna
  const groupedSubscriptions = useMemo(() => {
    if (!group?.courseSubscriptions) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset ore per confronto solo con la data
    
    const subscriptionsByUser = group.courseSubscriptions.reduce((acc, subscription) => {
      const userId = subscription.user.id;
      if (!acc[userId]) {
        acc[userId] = [];
      }
      acc[userId].push(subscription);
      return acc;
    }, {});

    return Object.entries(subscriptionsByUser).map(([userId, subscriptions]) => {
      // Filtra le subscription valide (endDate >= data odierna)
      const validSubscriptions = getValidSubscriptions(subscriptions);

      // Un abbonamento a ingressi esaurito non è realmente utilizzabile, quindi viene retrocesso
      const isExhausted = (sub) => {
        if (!isPayPerUseSubscription(sub)) return false;
        const status = entriesStatusMap[sub.id];
        return !!status && !status.loading && !status.error
          && status.usedEntries >= status.totalEntries;
      };

      // Ordine: valido utilizzabile > futuro utilizzabile > valido esaurito > futuro esaurito > scaduto
      const rank = (sub) => {
        const status = getSubscriptionStatus(sub, today);
        const exhausted = isExhausted(sub);
        if (status === 'valid') return exhausted ? 2 : 0;
        if (status === 'future') return exhausted ? 3 : 1;
        return 4;
      };

      const primarySubscription = subscriptions.length > 0
        ? [...subscriptions].sort((a, b) => {
            const rankDiff = rank(a) - rank(b);
            if (rankDiff !== 0) return rankDiff;

            // A parità di stato, i mensili hanno priorità sui pacchetti a ingressi
            const typeDiff = (isPayPerUseSubscription(a) ? 1 : 0) - (isPayPerUseSubscription(b) ? 1 : 0);
            if (typeDiff !== 0) return typeDiff;

            if (a.endDate && b.endDate) {
              return new Date(b.endDate) - new Date(a.endDate);
            }
            return 0;
          })[0]
        : null;

      // Ordina tutte le subscription per la visualizzazione dello storico
      const sortedSubscriptions = subscriptions.sort((a, b) => {
        // Prima quella primaria, poi per data di fine decrescente
        if (a.id === primarySubscription?.id) return -1;
        if (b.id === primarySubscription?.id) return 1;
        if (a.endDate && b.endDate) {
          return new Date(b.endDate) - new Date(a.endDate);
        }
        return 0;
      });

      return {
        user: subscriptions[0].user,
        primarySubscription: primarySubscription,
        allSubscriptions: sortedSubscriptions,
        hasMultiple: sortedSubscriptions.length > 1,
        activeCount: validSubscriptions.length,
        expiredCount: countExpiredSubscriptions(subscriptions),
        isArchived: subscriptions.some(sub => sub.isArchived)
      };
    }).sort((a, b) => {
      // Ordina per nome e cognome
      const aName = `${a.user.firstName} ${a.user.lastName}`.toLowerCase();
      const bName = `${b.user.firstName} ${b.user.lastName}`.toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [group?.courseSubscriptions, entriesStatusMap]);

  // Filtra i partecipanti in base allo stato di archiviazione
  const visibleSubscriptions = useMemo(() => {
    if (participantFilter === 'all') return groupedSubscriptions;
    if (participantFilter === 'archived') return groupedSubscriptions.filter(g => g.isArchived);
    return groupedSubscriptions.filter(g => !g.isArchived); // 'active'
  }, [groupedSubscriptions, participantFilter]);

  const visibleParticipantIds = useMemo(() => {
    return visibleSubscriptions.map(g => g.user.id);
  }, [visibleSubscriptions]);

  const allVisibleSelected = useMemo(() => {
    if (visibleParticipantIds.length === 0) {
      return false;
    }
    return visibleParticipantIds.every(userId => selectedParticipants.includes(userId));
  }, [visibleParticipantIds, selectedParticipants]);

  const hiddenSelectedCount = useMemo(() => {
    if (selectedParticipants.length === 0) {
      return 0;
    }
    const visibleIdsSet = new Set(visibleParticipantIds);
    return selectedParticipants.filter(userId => !visibleIdsSet.has(userId)).length;
  }, [selectedParticipants, visibleParticipantIds]);

  const archivedCount = useMemo(() => {
    return groupedSubscriptions.filter(g => g.isArchived).length;
  }, [groupedSubscriptions]);

  // Mantiene selezionati solo utenti ancora presenti nel gruppo dopo mutazioni/reload
  useEffect(() => {
    const validUserIds = new Set(groupedSubscriptions.map(g => g.user.id));
    setSelectedParticipants(prev => {
      const filtered = prev.filter(userId => validUserIds.has(userId));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [groupedSubscriptions]);

  // State per gestire l'espansione delle righe
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Carica status ingressi per subscription pay-per-use dopo il caricamento del gruppo.
  // Dipende solo dalle iscrizioni del gruppo (e non da groupedSubscriptions, che dipende da
  // entriesStatusMap) per evitare il ciclo render -> fetch -> setState -> render.
  useEffect(() => {
    const payPerUseSubscriptions = (group?.courseSubscriptions || []).filter(
      sub => isPayPerUseSubscription(sub)
    );

    if (payPerUseSubscriptions.length > 0) {
      loadEntriesStatus(payPerUseSubscriptions);
    }
  }, [group, loadEntriesStatus]);

  // Funzione per toggle dell'espansione
  const toggleRowExpansion = (userId) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSelectParticipant = (userId) => {
    setSelectedParticipants(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleSelectAllVisible = () => {
    setSelectedParticipants(prev => {
      if (visibleParticipantIds.length === 0) {
        return prev;
      }

      const visibleIdsSet = new Set(visibleParticipantIds);
      const allSelected = visibleParticipantIds.every(userId => prev.includes(userId));

      if (allSelected) {
        return prev.filter(userId => !visibleIdsSet.has(userId));
      }

      const next = [...prev];
      visibleParticipantIds.forEach(userId => {
        if (!next.includes(userId)) {
          next.push(userId);
        }
      });
      return next;
    });
  };

  // Utenti selezionati (oggetti User completi) per azioni massive
  const selectedUsers = useMemo(() => {
    if (selectedParticipants.length === 0) return [];
    const selectedSet = new Set(selectedParticipants);
    return groupedSubscriptions
      .filter(g => selectedSet.has(g.user.id))
      .map(g => g.user);
  }, [groupedSubscriptions, selectedParticipants]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento partecipanti...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">
          {error}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/groups')}>
          Torna ai Gruppi
        </Button>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">
          Gruppo non trovato
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/groups')}>
          Torna ai Gruppi
        </Button>
      </div>
    );
  }

  return (
    <div className="group-participants">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2>Partecipanti del Gruppo</h2>
          <div className="d-flex align-items-center gap-3 mb-3">
            <h4 className="mb-0 text-primary">{group.name}</h4>
            <Badge bg="info">
              {group.course?.name || 'Corso non specificato'}
            </Badge>
            {group.course?.academicYear && (
              <Badge bg="secondary">
                {group.course.academicYear}/{group.course.academicYear + 1}
              </Badge>
            )}
          </div>
          {group.description && (
            <p className="text-muted">{group.description}</p>
          )}
        </Col>
        <Col xs="auto">
          <div className="d-flex gap-2">
            <BulkUserActions
              selectedUsers={selectedUsers}
              onSuccess={(msg) => setSuccess(msg)}
              onError={(err) => setActionError(err)}
              onClearSelection={() => setSelectedParticipants([])}
              className=""
            />
            <ExportCSVButton 
              onExport={handleExportCSV}
              loading={exporting}
              disabled={loading || visibleSubscriptions.length === 0}
            />
            <Button
              variant="success"
              onClick={handleShowAddModal}
              disabled={group.maxParticipants && groupedSubscriptions.filter(g => g.activeCount > 0 && !g.isArchived).length >= group.maxParticipants}
            >
              <i className="fas fa-user-plus me-2"></i>
              Aggiungi Partecipante
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/groups')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Torna ai Gruppi
            </Button>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {actionError && (
        <Alert variant="warning" onClose={() => setActionError('')} dismissible>
          {actionError}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Statistiche */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary mb-1">{groupedSubscriptions.filter(g => g.activeCount > 0 && !g.isArchived).length}</h3>
              <p className="text-muted mb-0">Partecipanti Attivi</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info mb-1">
                {group.maxParticipants || '∞'}
              </h3>
              <p className="text-muted mb-0">Posti Disponibili</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success mb-1">
                {group.maxParticipants ? 
                  Math.max(0, group.maxParticipants - groupedSubscriptions.filter(g => g.activeCount > 0 && !g.isArchived).length) : 
                  '∞'
                }
              </h3>
              <p className="text-muted mb-0">Posti Liberi</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista partecipanti */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-users me-2"></i>
            Lista Partecipanti
          </h5>
          <div className="d-flex align-items-center gap-2">
            {archivedCount > 0 && (
              <small className="text-muted">Archiviati: {archivedCount}</small>
            )}
            <div className="btn-group btn-group-sm" role="group">
              <Button
                variant={participantFilter === 'active' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setParticipantFilter('active')}
              >
                Attivi
              </Button>
              <Button
                variant={participantFilter === 'all' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setParticipantFilter('all')}
              >
                Tutti
              </Button>
              <Button
                variant={participantFilter === 'archived' ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setParticipantFilter('archived')}
              >
                <i className="fas fa-archive me-1"></i>
                Archiviati
              </Button>
            </div>
            {hiddenSelectedCount > 0 && (
              <Badge bg="secondary">
                {hiddenSelectedCount} non visibili
              </Badge>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {visibleSubscriptions.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-user-plus fa-3x text-muted mb-3"></i>
              <p className="text-muted mb-0">
                {groupedSubscriptions.length === 0
                  ? 'Nessun partecipante iscritto al gruppo'
                  : participantFilter === 'archived'
                  ? 'Nessun partecipante archiviato'
                  : 'Tutti i partecipanti sono archiviati. Usa il filtro "Archiviati" per visualizzarli.'
                }
              </p>
            </div>
          ) : (
            <Table responsive className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: '50px' }} className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={handleSelectAllVisible}
                      disabled={visibleParticipantIds.length === 0}
                      aria-label="Seleziona tutti i partecipanti visibili"
                    />
                  </th>
                  <th>Atleta</th>
                  <th>Abbonamento</th>
                  <th className="text-center">Prezzo</th>
                  <th className="text-center">Data Inizio</th>
                  <th className="text-center">Data Fine</th>
                  <th className="text-center">Stato</th>
                  <th className="text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {visibleSubscriptions.map((group) => (
                  <React.Fragment key={group.user.id}>
                    {/* Riga principale */}
                    <tr className={group.isArchived ? 'table-warning opacity-75' : !isSubscriptionValid(group.primarySubscription) ? 'table-secondary' : ''}>
                      <td className="text-center align-middle">
                        <Form.Check
                          type="checkbox"
                          checked={selectedParticipants.includes(group.user.id)}
                          onChange={() => handleSelectParticipant(group.user.id)}
                          aria-label={`Seleziona ${group.user.firstName} ${group.user.lastName}`}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {group.hasMultiple && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              onClick={() => toggleRowExpansion(group.user.id)}
                              title={expandedRows.has(group.user.id) ? 'Nascondi storico' : 'Mostra storico'}
                            >
                              <i className={`fas fa-chevron-${expandedRows.has(group.user.id) ? 'down' : 'right'}`}></i>
                            </Button>
                          )}
                          <div>
                            <strong 
                              className="text-primary text-decoration-none cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/users/${group.user.id}/edit`)}
                              title="Clicca per visualizzare la scheda atleta"
                            >
                              {group.user.firstName} {group.user.lastName}
                            </strong>
                            {group.isArchived && (
                              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7em' }}>
                                <i className="fas fa-archive me-1"></i>Archiviato
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {group.primarySubscription.offer ? (
                          <div>
                            <span className="fw-medium">{group.primarySubscription.offer.name}</span>
                            {isTrialSubscription(group.primarySubscription) && (
                              <Badge bg="warning" text="dark" className="ms-2">
                                <i className="fas fa-stopwatch me-1"></i>Prova
                              </Badge>
                            )}
                            {isPayPerUseSubscription(group.primarySubscription) && (
                              <div>
                                <small className="text-muted d-block">Pay-per-use</small>
                                {entriesStatusMap[group.primarySubscription.id] ? (
                                  entriesStatusMap[group.primarySubscription.id].loading ? (
                                    <small className="text-muted d-block">
                                      <Spinner animation="border" size="sm" className="me-1" />
                                      Caricamento...
                                    </small>
                                  ) : entriesStatusMap[group.primarySubscription.id].error ? (
                                    <small className="text-danger d-block">
                                      <i className="fas fa-exclamation-circle me-1"></i>
                                      Errore caricamento
                                    </small>
                                  ) : (
                                    <small className="d-block">
                                      {entriesStatusMap[group.primarySubscription.id].usedEntries >= entriesStatusMap[group.primarySubscription.id].totalEntries ? (
                                        <Badge bg="danger" className="me-1">{entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi</Badge>
                                      ) : entriesStatusMap[group.primarySubscription.id].isRunningOut ? (
                                        <Badge bg="warning" text="dark" className="me-1">{entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi</Badge>
                                      ) : <Badge bg="success" className="me-1">{entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi</Badge>}
                                    </small>
                                  )
                                ) : null}
                              </div>
                            )}
                            {isMonthlyOffer(group.primarySubscription.offer) && (
                              <small className="text-muted d-block">
                                {group.primarySubscription.offer.durationMonths || 0} mes{group.primarySubscription.offer.durationMonths === 1 ? 'e' : 'i'}
                              </small>
                            )}
                            {!isPayPerUseSubscription(group.primarySubscription) && !isMonthlyOffer(group.primarySubscription.offer) && (
                              <small className="text-info d-block">Data Libera</small>
                            )}
                            {group.primarySubscription.hasInstallments && (
                              <div className="mt-1">
                                <Badge 
                                  bg="info" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setSelectedSubscriptionForInstallments(group.primarySubscription);
                                    setShowInstallmentsModal(true);
                                  }}
                                  title="Clicca per gestire le rate"
                                >
                                  <i className="fas fa-coins me-1"></i>
                                  A rate ({group.primarySubscription.installments?.filter(i => i.paymentDate).length || 0}/{group.primarySubscription.installments?.length || 0} pagate)
                                </Badge>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            {isTrialSubscription(group.primarySubscription) ? (
                              <div>
                                <Badge bg="warning" text="dark" className="fw-semibold">
                                  <i className="fas fa-stopwatch me-1"></i>Prova
                                </Badge>
                                {entriesStatusMap[group.primarySubscription.id] ? (
                                  entriesStatusMap[group.primarySubscription.id].loading ? (
                                    <small className="text-muted d-block mt-1">
                                      <Spinner animation="border" size="sm" className="me-1" />
                                      Caricamento...
                                    </small>
                                  ) : entriesStatusMap[group.primarySubscription.id].error ? (
                                    <small className="text-danger d-block mt-1">
                                      <i className="fas fa-exclamation-circle me-1"></i>
                                      Errore
                                    </small>
                                  ) : (
                                    <small className="d-block mt-1">
                                      {entriesStatusMap[group.primarySubscription.id].usedEntries >= entriesStatusMap[group.primarySubscription.id].totalEntries ? (
                                        <Badge bg="danger" className="me-1">{entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi</Badge>
                                      ) : entriesStatusMap[group.primarySubscription.id].isRunningOut ? (
                                        <Badge bg="warning" text="dark" className="me-1">{entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi</Badge>
                                      ) : <Badge bg="success" className="me-1">{entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi</Badge>}
                                    </small>
                                  )
                                ) : (
                                  <small className="text-muted d-block mt-1">
                                    {group.primarySubscription.totalTickets ?? 1} {group.primarySubscription.totalTickets === 1 ? 'ingresso' : 'ingressi'}
                                  </small>
                                )}
                              </div>
                            ) : (
                              <div>
                                <span className="fw-medium text-info">
                                  Data Libera {group.primarySubscription.totalTickets ? `(${group.primarySubscription.totalTickets} ingressi)` : ''}
                                </span>
                                {isPayPerUseSubscription(group.primarySubscription) && (
                                  <div className="mt-1">
                                    {entriesStatusMap[group.primarySubscription.id] && !entriesStatusMap[group.primarySubscription.id].loading && !entriesStatusMap[group.primarySubscription.id].error ? (
                                      <small className="d-block">
                                        <Badge bg="success" className="me-1">
                                          {entriesStatusMap[group.primarySubscription.id].usedEntries}/{entriesStatusMap[group.primarySubscription.id].totalEntries} ingressi
                                        </Badge>
                                      </small>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="fw-bold text-success">
                          €{group.primarySubscription.price?.toFixed(2) || '0.00'}
                        </span>
                      </td>
                      <td className="text-center">
                        <small className="text-muted">
                          {group.primarySubscription.startDate ? new Date(group.primarySubscription.startDate).toLocaleDateString('it-IT') : 'N/A'}
                        </small>
                      </td>
                      <td className="text-center">
                        <small className={
                          group.primarySubscription.endDate && new Date(group.primarySubscription.endDate) < new Date()
                            ? "text-danger fw-bold"
                            : "text-muted"
                        }>
                          {group.primarySubscription.endDate ? new Date(group.primarySubscription.endDate).toLocaleDateString('it-IT') : 'N/A'}
                        </small>
                      </td>
                      <td className="text-center">
                        {(() => {
                          const statusBadge = getSubscriptionStatusBadge(group.primarySubscription);
                          return (
                            <Badge 
                              bg={statusBadge.variant}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleShowNewSubscriptionModal(group.user)}
                              title="Clicca per aggiungere una nuova iscrizione"
                            >
                              {statusBadge.text}
                            </Badge>
                          );
                        })()} 
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveParticipant(group.primarySubscription)}
                            disabled={actionLoading[`remove-${group.primarySubscription.id}`]}
                            title="Elimina iscrizione"
                          >
                            {actionLoading[`remove-${group.primarySubscription.id}`] ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <i className="fas fa-trash-alt"></i>
                            )}
                          </Button>
                          <Dropdown>
                            <Dropdown.Toggle 
                              variant="outline-secondary" 
                              size="sm" 
                              id={`dropdown-${group.user.id}`}
                              className="px-2"
                            >
                              <i className="fas fa-ellipsis-v"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleShowNewSubscriptionModal(group.user)}>
                                <i className="fas fa-plus me-2"></i>
                                Nuova iscrizione
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleShowEditSubscriptionModal(group.primarySubscription)}>
                                <i className="fas fa-edit me-2"></i>
                                Modifica iscrizione
                              </Dropdown.Item>
                              {group.primarySubscription?.hasInstallments && (
                                <Dropdown.Item onClick={() => {
                                  setSelectedSubscriptionForInstallments(group.primarySubscription);
                                  setShowInstallmentsModal(true);
                                }}>
                                  <i className="fas fa-coins me-2 text-primary"></i>
                                  Gestisci rate
                                </Dropdown.Item>
                              )}
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => navigate(`/users/${group.user.id}/edit`)}>
                                <i className="fas fa-user me-2"></i>
                                Dettagli atleta
                              </Dropdown.Item>
                              {group.hasMultiple && (
                                <Dropdown.Item onClick={() => toggleRowExpansion(group.user.id)}>
                                  <i className={`fas fa-${expandedRows.has(group.user.id) ? 'eye-slash' : 'eye'} me-2`}></i>
                                  {expandedRows.has(group.user.id) ? 'Nascondi storico' : 'Mostra storico'}
                                </Dropdown.Item>
                              )}
                              <Dropdown.Divider />
                              <Dropdown.Item 
                                onClick={() => handleArchiveUser(
                                  group.user.id, 
                                  `${group.user.firstName} ${group.user.lastName}`,
                                  !group.isArchived
                                )}
                                disabled={actionLoading[`archive-${group.user.id}`] || (!group.isArchived && group.activeCount > 0)}
                                title={!group.isArchived && group.activeCount > 0 ? 'Impossibile archiviare: l\'atleta ha abbonamenti ancora validi' : undefined}
                              >
                                {actionLoading[`archive-${group.user.id}`] ? (
                                  <Spinner animation="border" size="sm" className="me-2" />
                                ) : (
                                  <i className={`fas fa-${group.isArchived ? 'undo' : 'archive'} me-2`}></i>
                                )}
                                {group.isArchived ? 'Ripristina' : 'Archivia'}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Righe espanse per lo storico */}
                    {group.hasMultiple && expandedRows.has(group.user.id) && (
                      <>
                        {group.allSubscriptions.slice(1).map((subscription, index) => (
                          <tr key={`${group.user.id}-${subscription.id}`} className="table-light">
                            <td></td>
                            <td className="ps-5">
                            </td>
                            <td>
                              {subscription.offer ? (
                                <div>
                                  <small className="text-muted d-block">{subscription.offer.name}</small>
                                  {isPayPerUseOffer(subscription.offer) && (
                                    <div>
                                      <small className="text-muted d-block">Pay-per-use</small>
                                      {entriesStatusMap[subscription.id] ? (
                                        entriesStatusMap[subscription.id].loading ? (
                                          <small className="text-muted d-block">
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            Caricamento...
                                          </small>
                                        ) : entriesStatusMap[subscription.id].error ? (
                                          <small className="text-danger d-block">
                                            <i className="fas fa-exclamation-circle me-1"></i>
                                            Errore
                                          </small>
                                        ) : (
                                          <small className="d-block">
                                            {entriesStatusMap[subscription.id].usedEntries >= entriesStatusMap[subscription.id].totalEntries ? (
                                              <Badge bg="danger" className="me-1">{entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi</Badge>
                                            ) : entriesStatusMap[subscription.id].remainingEntries < 5 ? (
                                              <Badge bg="warning" text="dark" className="me-1">{entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi</Badge>
                                            ) : <Badge bg="success" className="me-1">{entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi</Badge>}
                                          </small>
                                        )
                                      ) : null}
                                    </div>
                                  )}
                                  {isMonthlyOffer(subscription.offer) && (
                                    <small className="text-muted d-block">
                                      {subscription.offer.durationMonths || 0} mes{subscription.offer.durationMonths === 1 ? 'e' : 'i'}
                                    </small>
                                  )}
                                  {!isPayPerUseOffer(subscription.offer) && !isMonthlyOffer(subscription.offer) && (
                                    <small className="text-info d-block">Data Libera</small>
                                  )}
                                  {subscription.hasInstallments && (
                                    <div className="mt-1">
                                      <Badge 
                                        bg="info" 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                          setSelectedSubscriptionForInstallments(subscription);
                                          setShowInstallmentsModal(true);
                                        }}
                                        title="Clicca per gestire le rate"
                                      >
                                        <i className="fas fa-coins me-1"></i>
                                        A rate ({subscription.installments?.filter(i => i.paymentDate).length || 0}/{subscription.installments?.length || 0})
                                      </Badge>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  {isTrialSubscription(subscription) ? (
                                    <div>
                                      <Badge bg="warning" text="dark" style={{ fontSize: '0.75rem' }}>
                                        <i className="fas fa-stopwatch me-1"></i>Prova
                                      </Badge>
                                      {entriesStatusMap[subscription.id] && !entriesStatusMap[subscription.id].loading && !entriesStatusMap[subscription.id].error ? (
                                        <small className="d-block mt-1">
                                          <Badge bg="success" className="me-1">
                                            {entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi
                                          </Badge>
                                        </small>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div>
                                      <small className="fw-medium text-info">
                                        Data Libera {subscription.totalTickets ? `(${subscription.totalTickets} ingressi)` : ''}
                                      </small>
                                      {isPayPerUseSubscription(subscription) && (
                                        <div className="mt-1">
                                          {entriesStatusMap[subscription.id] && !entriesStatusMap[subscription.id].loading && !entriesStatusMap[subscription.id].error ? (
                                            <small className="d-block">
                                              <Badge bg="success" className="me-1">
                                                {entriesStatusMap[subscription.id].usedEntries}/{entriesStatusMap[subscription.id].totalEntries} ingressi
                                              </Badge>
                                            </small>
                                          ) : null}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {subscription.hasInstallments && (
                                    <div className="mt-1">
                                      <Badge 
                                        bg="info" 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                          setSelectedSubscriptionForInstallments(subscription);
                                          setShowInstallmentsModal(true);
                                        }}
                                        title="Clicca per gestire le rate"
                                      >
                                        <i className="fas fa-coins me-1"></i>
                                        A rate
                                      </Badge>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="text-center">
                              <small className="text-success">
                                €{subscription.price?.toFixed(2) || '0.00'}
                              </small>
                            </td>
                            <td className="text-center">
                              <small className="text-muted">
                                {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString('it-IT') : 'N/A'}
                              </small>
                            </td>
                            <td className="text-center">
                              <small className={
                                subscription.endDate && new Date(subscription.endDate) < new Date()
                                  ? "text-danger"
                                  : "text-muted"
                              }>
                                {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString('it-IT') : 'N/A'}
                              </small>
                            </td>
                            <td className="text-center">
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleShowEditSubscriptionModal(subscription)}
                                title="Modifica iscrizione"
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal per aggiunta partecipanti */}
      <Modal show={showAddModal} onHide={handleCloseAddModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Modifica Iscrizione' : 
             isNewSubscriptionMode ? 'Aggiungi Nuova Iscrizione' : 
             'Aggiungi Partecipante al Gruppo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Alert di successo nella modale */}
          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-3 fw-bold">
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          )}
          
          {/* Alert di errore nella modale */}
          {actionError && (
            <Alert variant="warning" onClose={() => setActionError('')} dismissible className="mb-3 fw-bold">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {actionError}
            </Alert>
          )}
          
          {/* Errore specifico della modale */}
          {modalError && (
            <Alert variant="danger" onClose={() => setModalError('')} dismissible className="mb-3 fw-bold">
              <i className="fas fa-exclamation-circle me-2"></i>
              {modalError}
            </Alert>
          )}
          
          <Form>
            {!isNewSubscriptionMode && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Seleziona Utente *</Form.Label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={false}
                      loadOptions={loadUsersAsync}
                      onChange={setSelectedUser}
                      value={selectedUser}
                      placeholder="Cerca utente per nome o email..."
                      noOptionsMessage={({ inputValue }) => 
                        !inputValue || inputValue.length < 2
                          ? "Digita almeno 2 caratteri per cercare..."
                          : "Nessun utente trovato"
                      }
                      loadingMessage={() => "Ricerca in corso..."}
                      isClearable
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: '38px'
                        })
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {(isNewSubscriptionMode || isEditMode) && selectedUser && (
              <Row>
                <Col md={12}>
                  <Alert variant="info" className="mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    {isEditMode ? 
                      `Stai modificando l'iscrizione di: ` : 
                      `Stai aggiungendo una nuova iscrizione per: `
                    }
                    <strong>{selectedUser.user?.firstName} {selectedUser.user?.lastName}</strong>
                  </Alert>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Seleziona Abbonamento (opzionale)
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadOffersAsync}
                    onChange={handleOfferSelect}
                    value={selectedOffer}
                    placeholder="Seleziona un'abbonamento (opzionale)"
                    noOptionsMessage={({ inputValue }) => 
                      inputValue ? "Nessun abbonamento trovato" : "Digita per cercare o visualizza tutti"
                    }
                    loadingMessage={() => "Caricamento abbonamenti..."}
                    isClearable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '38px'
                      })
                    }}
                  />
                  <Form.Text className="text-muted">
                    Seleziona un'abbonamento per impostare automaticamente il prezzo e la durata dell'iscrizione, oppure gestisci manualmente
                  </Form.Text>
                  {!selectedOffer && (
                    <Alert variant="info" className="mt-2 mb-0">
                      <small>
                        <i className="fas fa-info-circle me-1"></i>
                        Nessun abbonamento selezionato. Puoi gestire prezzo e date manualmente.
                      </small>
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {selectedOffer?.offer?.hasInstallments && selectedOffer?.value !== 'TRIAL' && (
              <Row className="mb-3">
                <Col md={12}>
                  <div className="p-3 bg-light rounded border">
                    <Form.Label className="fw-bold d-block text-primary">
                      <i className="fas fa-coins me-2"></i>
                      Modalità di Pagamento
                    </Form.Label>
                    <div className="d-flex gap-4">
                      <Form.Check
                        type="radio"
                        id="add-payment-mode-single"
                        name="addPaymentMode"
                        label={<span>Pagamento unico (<strong>€{selectedOffer.offer.amount.toFixed(2)}</strong>)</span>}
                        checked={!isInstallmentPlan}
                        onChange={() => handleToggleInstallmentPlan(false)}
                      />
                      <Form.Check
                        type="radio"
                        id="add-payment-mode-installments"
                        name="addPaymentMode"
                        label={<span>Pagamento a rate (<strong>€{(selectedOffer.offer.installmentTotalAmount != null ? selectedOffer.offer.installmentTotalAmount : selectedOffer.offer.amount).toFixed(2)}</strong> in {selectedOffer.offer.installmentCount || 2} rate)</span>}
                        checked={isInstallmentPlan}
                        onChange={() => handleToggleInstallmentPlan(true)}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {selectedOffer?.value === 'TRIAL' ? (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Scadenza *</Form.Label>
                      <Form.Control
                        type="date"
                        value={subscriptionData.endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          if (newEndDate && subscriptionData.startDate && newEndDate < subscriptionData.startDate) {
                            setModalError('La data di scadenza non può essere precedente alla data di inizio');
                            setTimeout(() => setModalError(''), 5000);
                            return;
                          }
                          setModalError('');
                          setSubscriptionData(prev => ({
                            ...prev,
                            endDate: newEndDate
                          }));
                        }}
                        min={subscriptionData.startDate}
                        required
                      />
                      <Form.Text className="text-muted">
                        Default: 1 settimana dalla data odierna
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Numero di Ingressi *</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={totalTickets}
                        onChange={(e) => setTotalTickets(e.target.value)}
                        placeholder="1"
                        required
                      />
                      <Form.Text className="text-muted">
                        Numero di lezioni di prova consentite (default: 1)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={subscriptionData.notes}
                        onChange={(e) => setSubscriptionData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))}
                        placeholder="Note aggiuntive per la prova..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Inizio *</Form.Label>
                      <Form.Control
                        type="date"
                        value={subscriptionData.startDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        min={isNewSubscriptionMode ? subscriptionData.startDate : undefined}
                        required
                      />
                      {isNewSubscriptionMode && (
                        <Form.Text className="text-muted">
                          Data calcolata automaticamente dal termine dell'ultima iscrizione
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Fine *</Form.Label>
                      <Form.Control
                        type="date"
                        value={subscriptionData.endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          if (newEndDate && subscriptionData.startDate && newEndDate < subscriptionData.startDate) {
                            setModalError('La data di fine non può essere precedente alla data di inizio');
                            setTimeout(() => setModalError(''), 5000);
                            return;
                          }
                          setModalError('');
                          setSubscriptionData(prev => ({
                            ...prev,
                            endDate: newEndDate
                          }));
                        }}
                        min={subscriptionData.startDate}
                        required
                      />
                      {selectedOffer && (
                        <Form.Text className="text-info">
                          Data calcolata automaticamente dall'abbonamento selezionato
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {(!selectedOffer || selectedOffer.value === 'FREE_DATE') && (
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="custom-pay-per-use-check"
                          label="Abbonamento a ingressi"
                          checked={isCustomPayPerUse}
                          onChange={(e) => {
                            setIsCustomPayPerUse(e.target.checked);
                            if (!e.target.checked) {
                              setTotalTickets('');
                            } else if (!totalTickets || totalTickets === 1) {
                              setTotalTickets(10);
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {isCustomPayPerUse && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Numero di Ingressi *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={totalTickets}
                          onChange={(e) => setTotalTickets(e.target.value)}
                          placeholder="es. 10"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{isInstallmentPlan ? 'Prezzo Totale (€)' : 'Prezzo (€)'}</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={subscriptionData.price}
                        onChange={(e) => setSubscriptionData(prev => ({
                          ...prev,
                          price: e.target.value
                        }))}
                        placeholder="0.00"
                      />
                      {isInstallmentPlan ? (
                        <Form.Text className={hasSumMismatch ? 'text-danger fw-bold' : 'text-success'}>
                          {hasSumMismatch
                            ? `Attenzione: deve coincidere con la somma delle rate (€${installmentsSum.toFixed(2)})`
                            : `Coincide con la somma delle rate (€${installmentsSum.toFixed(2)})`}
                        </Form.Text>
                      ) : selectedOffer ? (
                        <Form.Text className="text-info">
                          Prezzo impostato dall'abbonamento selezionato
                        </Form.Text>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={subscriptionData.notes}
                        onChange={(e) => setSubscriptionData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))}
                        placeholder="Note aggiuntive..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {!isInstallmentPlan && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Data Pagamento</Form.Label>
                        <Form.Control
                          type="date"
                          value={subscriptionData.paymentDate}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSubscriptionData(prev => ({
                              ...prev,
                              paymentDate: val,
                              paymentMethod: val ? (prev.paymentMethod || 'cash') : ''
                            }));
                          }}
                        />
                        <Form.Text className="text-muted">
                          Lascia vuoto se l'atleta non ha ancora pagato
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {subscriptionData.paymentDate && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Metodo Pagamento</Form.Label>
                          <Form.Select
                            value={subscriptionData.paymentMethod || 'cash'}
                            onChange={(e) => setSubscriptionData(prev => ({
                              ...prev,
                              paymentMethod: e.target.value
                            }))}
                          >
                            {PAYMENT_METHODS.map(pm => (
                              <option key={pm.value} value={pm.value}>{pm.label}</option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Tipo di pagamento utilizzato
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}

                {isInstallmentPlan && (
                  <Card className="mb-4 border-info">
                    <Card.Header className="bg-info bg-opacity-10 d-flex justify-content-between align-items-center py-2">
                      <div className="fw-bold text-dark">
                        <i className="fas fa-coins me-2 text-primary"></i>
                        Piano Rateizzazione ({installments.length} rate)
                      </div>
                      <div className="small">
                        Somma rate: <strong>€{installmentsSum.toFixed(2)}</strong> / Totale: <strong>€{currentPrice.toFixed(2)}</strong>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-3">
                      {installments.some(i => !!i.paymentDate) && (
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Metodo Pagamento Abbonamento</Form.Label>
                              <Form.Select
                                value={subscriptionData.paymentMethod || 'cash'}
                                onChange={(e) => setSubscriptionData(prev => ({
                                  ...prev,
                                  paymentMethod: e.target.value
                                }))}
                              >
                                {PAYMENT_METHODS.map(pm => (
                                  <option key={pm.value} value={pm.value}>{pm.label}</option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Metodo di pagamento condiviso per le rate dell'abbonamento
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      {hasSumMismatch && (
                        <Alert variant="warning" className="py-2 px-3 mb-3">
                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                            <div>
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              <strong>Attenzione:</strong> La somma delle rate (€{installmentsSum.toFixed(2)}) non corrisponde al totale (€{currentPrice.toFixed(2)}). Differenza: {sumDiff > 0 ? `+€${sumDiff.toFixed(2)}` : `-€${Math.abs(sumDiff).toFixed(2)}`}.
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleAdaptPriceToSum}
                              >
                                Adatta totale a somma rate
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRecalculateEqualInstallments}
                              >
                                Ricalcola rate uguali
                              </Button>
                            </div>
                          </div>
                        </Alert>
                      )}

                      <Table size="sm" responsive className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '12%' }}>Rata</th>
                            <th style={{ width: '24%' }}>Periodo coperto</th>
                            <th style={{ width: '18%' }}>Scadenza *</th>
                            <th style={{ width: '16%' }}>Importo (€) *</th>
                            <th style={{ width: '30%' }}>Pagamento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {installments.map((inst, index) => {
                            const { start: prevCoverageDate, end: periodEnd } = getInstallmentCoveragePeriod(
                              inst,
                              index,
                              installments,
                              subscriptionData.startDate,
                              subscriptionData.endDate
                            );
                            const isPaid = !!inst.paymentDate;

                            return (
                              <tr key={index}>
                                <td className="fw-bold">
                                  Rata {inst.installmentNumber}
                                  {isPaid && (
                                    <div><Badge bg="success" className="text-uppercase" style={{ fontSize: '0.65rem' }}>Pagata</Badge></div>
                                  )}
                                </td>
                                <td className="small text-muted">
                                  {prevCoverageDate ? new Date(prevCoverageDate).toLocaleDateString('it-IT') : '-'}
                                  {' → '}
                                  {periodEnd ? new Date(periodEnd).toLocaleDateString('it-IT') : '-'}
                                </td>
                                <td>
                                  <Form.Control
                                    type="date"
                                    size="sm"
                                    value={inst.dueDate}
                                    onChange={(e) => handleInstallmentChange(index, 'dueDate', e.target.value)}
                                    required
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    min="0"
                                    value={inst.amount}
                                    onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                                    required
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <Form.Control
                                      type="date"
                                      size="sm"
                                      value={inst.paymentDate}
                                      onChange={(e) => handleInstallmentChange(index, 'paymentDate', e.target.value)}
                                      placeholder="Non pagata"
                                    />
                                    {!isPaid ? (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="text-nowrap"
                                        onClick={() => handleMarkInstallmentPaidToday(index)}
                                        title="Segna come pagata oggi"
                                      >
                                        Oggi
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleClearInstallmentPayment(index)}
                                        title="Annulla pagamento"
                                      >
                                        <i className="fas fa-times"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}

                {/* Campo correzione ingressi utilizzati - per abbonamenti a ingresso */}
                {(selectedOffer?.offer?.totalTickets != null || isCustomPayPerUse) && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Correzione Ingressi Utilizzati
                          <i className="fas fa-info-circle ms-2 text-info" title="Correzione manuale del conteggio ingressi. Usa valori positivi per aumentare gli ingressi usati, negativi per diminuirli. Influenza il conteggio totale degli ingressi per l'anno sportivo."></i>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="1"
                          value={subscriptionData.usedEntriesAdjustment}
                          onChange={(e) => setSubscriptionData(prev => ({
                            ...prev,
                            usedEntriesAdjustment: parseInt(e.target.value) || 0
                          }))}
                          placeholder="0"
                        />
                        <Form.Text className="text-muted">
                          {isEditMode 
                            ? "Modifica il conteggio degli ingressi già utilizzati"
                            : "Imposta un valore iniziale di ingressi utilizzati (es. se l'atleta ha già fatto lezioni)"
                          }
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseAddModal}
            disabled={actionLoading['add-participant']}
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleAddParticipant}
            disabled={
              actionLoading['add-participant'] ||
              !selectedUser?.value ||
              !subscriptionData.startDate ||
              !subscriptionData.endDate ||
              (selectedOffer?.value === 'TRIAL' && (!totalTickets || parseInt(totalTickets, 10) < 1)) ||
              (isCustomPayPerUse && (!totalTickets || parseInt(totalTickets, 10) < 1)) ||
              (selectedOffer?.value !== 'TRIAL' && isInstallmentPlan && hasSumMismatch)
            }
          >
            {actionLoading['add-participant'] && <Spinner animation="border" size="sm" className="me-2" />}
            {isEditMode ? 'Salva Modifiche' : 
             isNewSubscriptionMode ? 'Aggiungi Nuova Iscrizione' : 
             'Aggiungi Partecipante'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modale di avviso per abbonamenti incompatibili */}
      <Modal show={showIncompatibleModal} onHide={handleCloseIncompatibleModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Abbonamenti Incompatibili
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <h5 className="alert-heading">
              <i className="fas fa-info-circle me-2"></i>
              Attenzione: Sovrapposizione temporale rilevata
            </h5>
            <p className="mb-0">
              L'atleta ha già degli abbonamenti a ingressi per questo gruppo che si <strong>sovrappongono temporalmente</strong> con il periodo del nuovo abbonamento mensile. 
              Gli abbonamenti a ingressi sono <strong>incompatibili</strong> con gli abbonamenti mensili durante lo stesso periodo.
            </p>
          </Alert>

          <h6 className="mb-3">Abbonamenti a ingressi che si sovrappongono da rimuovere:</h6>
          
          {incompatibleSubscriptions.length > 0 && (
            <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
              {incompatibleSubscriptions.map((sub, index) => (
                <div key={sub.id} className={index > 0 ? 'mt-3 pt-3 border-top' : ''}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        <Badge bg="secondary" className="me-2">
                          {sub.offer?.name || 'Abbonamento'}
                        </Badge>
                      </h6>
                      <div className="text-muted small">
                        <div>
                          <i className="fas fa-ticket-alt me-2"></i>
                          {sub.offer?.totalTickets} ingressi
                        </div>
                        <div>
                          <i className="fas fa-calendar me-2"></i>
                          Dal {new Date(sub.startDate).toLocaleDateString('it-IT')} al{' '}
                          {new Date(sub.endDate).toLocaleDateString('it-IT')}
                        </div>
                        <div>
                          <i className="fas fa-euro-sign me-2"></i>
                          {sub.price?.toFixed(2)} €
                        </div>
                        {sub.notes && (
                          <div>
                            <i className="fas fa-sticky-note me-2"></i>
                            {sub.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Alert variant="info" className="mb-0">
            <h6 className="mb-2">
              <i className="fas fa-lightbulb me-2"></i>
              Come procedere:
            </h6>
            <ol className="mb-0 ps-3">
              <li>Rimuovi manualmente gli abbonamenti a ingressi elencati sopra</li>
              <li>Riprova ad aggiungere l'abbonamento mensile</li>
            </ol>
            <p className="mt-2 mb-0">
              <small className="text-muted">
                <strong>Nota:</strong> Questa operazione è necessaria per evitare conflitti nella gestione delle presenze.
              </small>
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseIncompatibleModal}
          >
            <i className="fas fa-times me-2"></i>
            Ho capito
          </Button>
        </Modal.Footer>
      </Modal>

      <EditSubscriptionModal
        show={showEditModal}
        onHide={() => { setShowEditModal(false); setEditingSubscription(null); }}
        subscription={editingSubscription}
        groupId={id}
        courseId={group?.course?.id}
        onSuccess={(updatedSubscription) => {
          // Invalida la cache degli ingressi per la subscription modificata
          if (editingSubscription) {
            const userId = editingSubscription.user?.id;
            const subscriptionIds = userId
              ? (group?.courseSubscriptions || [])
                  .filter(sub => sub.user?.id === userId)
                  .map(sub => sub.id)
              : [editingSubscription.id];
            invalidateEntriesStatus(subscriptionIds);
          }
          loadGroupData();
        }}
      />

      {/* Modal Gestione Rapida Rate */}
      <Modal
        show={showInstallmentsModal}
        onHide={() => {
          setShowInstallmentsModal(false);
          setSelectedSubscriptionForInstallments(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-coins me-2 text-primary"></i>
            Rate Iscrizione - {selectedSubscriptionForInstallments?.user?.firstName} {selectedSubscriptionForInstallments?.user?.lastName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubscriptionForInstallments && (
            <>
              <div className="mb-3 d-flex justify-content-between align-items-center bg-light p-3 rounded">
                <div>
                  <strong>Offerta:</strong> {selectedSubscriptionForInstallments.offer?.name || 'Iscrizione'}
                  <div className="text-muted small">
                    Periodo: {new Date(selectedSubscriptionForInstallments.startDate).toLocaleDateString('it-IT')} - {new Date(selectedSubscriptionForInstallments.endDate).toLocaleDateString('it-IT')}
                  </div>
                </div>
                <div className="text-end">
                  <span className="fs-5 fw-bold text-success">€{selectedSubscriptionForInstallments.price?.toFixed(2)}</span>
                  <div className="small text-muted">
                    {selectedSubscriptionForInstallments.installments?.filter(i => i.paymentDate).length || 0} di {selectedSubscriptionForInstallments.installments?.length || 0} rate saldate
                  </div>
                </div>
              </div>

              <Table responsive hover size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Rata</th>
                    <th>Periodo coperto</th>
                    <th>Scadenza</th>
                    <th>Importo</th>
                    <th>Stato</th>
                    <th className="text-end">Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sortedInstallments = (selectedSubscriptionForInstallments.installments || [])
                      .slice()
                      .sort((a, b) => (a.installmentNumber || 0) - (b.installmentNumber || 0));

                    if (sortedInstallments.length === 0) {
                      return (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">
                            Nessuna rata configurata per questa iscrizione.
                          </td>
                        </tr>
                      );
                    }

                    return sortedInstallments.map((inst, index) => {
                      const { start: prevDate, end: periodEnd } = getInstallmentCoveragePeriod(
                        inst,
                        index,
                        sortedInstallments,
                        selectedSubscriptionForInstallments.startDate,
                        selectedSubscriptionForInstallments.endDate
                      );
                      const isPaid = !!inst.paymentDate;
                      const isOverdue = !isPaid && inst.dueDate && new Date(inst.dueDate) < new Date();
                      const isExpiring = !isPaid && !isOverdue && inst.dueDate && (new Date(inst.dueDate) - new Date() <= 30 * 86400000);

                      return (
                        <tr key={inst.id || index} className={isOverdue ? 'table-danger' : ''}>
                          <td className="fw-bold">Rata {inst.installmentNumber}</td>
                          <td className="small text-muted">
                            {prevDate ? new Date(prevDate).toLocaleDateString('it-IT') : '-'}
                            {' → '}
                            {periodEnd ? new Date(periodEnd).toLocaleDateString('it-IT') : '-'}
                          </td>
                          <td>
                            <span className={isOverdue ? 'text-danger fw-bold' : ''}>
                              {inst.dueDate ? new Date(inst.dueDate).toLocaleDateString('it-IT') : '-'}
                            </span>
                          </td>
                          <td className="fw-bold">€{inst.amount?.toFixed(2)}</td>
                          <td>
                            {isPaid ? (
                              <Badge bg="success">
                                <i className="fas fa-check me-1"></i>
                                Pagata il {new Date(inst.paymentDate).toLocaleDateString('it-IT')}
                                {selectedSubscriptionForInstallments.paymentMethod && (
                                  <span className="ms-1">({formatPaymentMethod(selectedSubscriptionForInstallments.paymentMethod)})</span>
                                )}
                              </Badge>
                            ) : isOverdue ? (
                              <Badge bg="danger">Scaduta</Badge>
                            ) : isExpiring ? (
                              <Badge bg="warning" text="dark">In scadenza</Badge>
                            ) : (
                              <Badge bg="secondary">Da pagare</Badge>
                            )}
                          </td>
                          <td className="text-end">
                            {!isPaid ? (
                              <Button
                                size="sm"
                                variant="outline-success"
                                disabled={actionLoading[`pay-inst-${inst.id}`]}
                                onClick={() => handleQuickPayInstallment(inst)}
                              >
                                {actionLoading[`pay-inst-${inst.id}`] ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <i className="fas fa-check me-1"></i>
                                    Segna pagata
                                  </>
                                )}
                              </Button>
                            ) : (
                              <span className="text-muted small"><i className="fas fa-check text-success me-1"></i>Saldata</span>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowInstallmentsModal(false);
              setSelectedSubscriptionForInstallments(null);
            }}
          >
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal di selezione metodo pagamento per la rata */}
      <Modal
        show={!!payingInstallment}
        onHide={() => setPayingInstallment(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">
            <i className="fas fa-receipt me-2 text-success"></i>
            Registra Pagamento Rata
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {payingInstallment && (
            <div>
              <p className="small mb-3">
                Rata #{payingInstallment.installment.installmentNumber} - Importo: <strong>€{payingInstallment.installment.amount?.toFixed(2)}</strong>
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Data Pagamento</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={payingInstallment.paymentDate}
                  onChange={(e) => setPayingInstallment(prev => ({ ...prev, paymentDate: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold">Metodo Pagamento</Form.Label>
                <Form.Select
                  size="sm"
                  value={payingInstallment.paymentMethod}
                  onChange={(e) => setPayingInstallment(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {PAYMENT_METHODS.map(pm => (
                    <option key={pm.value} value={pm.value}>{pm.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                  Metodo di pagamento dell'abbonamento
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setPayingInstallment(null)}>
            Annulla
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={!payingInstallment?.paymentDate || actionLoading[`pay-inst-${payingInstallment?.installment?.id}`]}
            onClick={handleConfirmQuickPayInstallment}
          >
            {actionLoading[`pay-inst-${payingInstallment?.installment?.id}`] ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Conferma Pagamento'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GroupParticipants;